/**
 * Reduce authored feedback markup to the string a screen reader should say.
 *
 * Visible feedback goes through `RichText`, which parses the markup into real
 * elements. A live region is a text node instead, so the same markup written
 * there would be spoken as literal tag names ("less-than b greater-than").
 * Rendering `RichText` a second time inside the live region is not an option:
 * it turns glossary markup into focusable `role="button"` elements, which would
 * duplicate tab stops.
 */

/** Spoken words substituted for `<sup>` / `<sub>`, so they can be translated. */
export interface SpokenTextLabels {
  /** Word spoken before superscript content, e.g. `" superscript "`. */
  superscript: string;
  /** Word spoken before subscript content, e.g. `" subscript "`. */
  subscript: string;
}

export const defaultSpokenTextLabels: SpokenTextLabels = {
  superscript: " superscript ",
  subscript: " subscript ",
};

/** `<` only opens a tag when a letter or `/` follows it. */
const TAG_START = /<\/?[a-zA-Z]/;

/**
 * Semantic sup/sub become spoken words: science copy relies on them for meaning,
 * and stripping the tags alone would turn "x<sup>2</sup>" into "x2" ("x two").
 */
const expandSupSub = (text: string, labels: SpokenTextLabels): string =>
  text
    .replace(/<sup[^>]*>(.*?)<\/sup>/gi, `${labels.superscript}$1`)
    .replace(/<sub[^>]*>(.*?)<\/sub>/gi, `${labels.subscript}$1`);

/**
 * Line-break and block-level markup, opening tags included. Both ends of a block
 * are a boundary: `"Not quite.<p>Check row 2</p>"` fuses into one word if only
 * the closing tag is spaced. Inline tags are deliberately absent, or emphasised
 * words would split ("the <b>in</b>dependent variable").
 */
const BLOCK_OR_BREAK_TAG =
  /<br\s*\/?>|<\/?(?:p|div|li|ul|ol|dl|dt|dd|tr|td|th|table|thead|tbody|tfoot|blockquote|section|figure|figcaption|h[1-6])\b[^>]*>/gi;

/**
 * A `<` that reaches no `>` before the next `<` is a comparison, not a tag —
 * "if a<b then". The HTML parser discards an unterminated tag *and everything
 * after it*, so those are escaped back to text before it sees them. The regex
 * this was ported from required a closing `>` and so never hit this.
 */
const escapeStrayLt = (markup: string): string =>
  markup.replace(/<(?![^<]*>)/g, "&lt;");

/**
 * Block markup becomes a space first, because it renders as a break but
 * contributes no whitespace to `textContent` ("term<br/>matters" would be
 * spoken as one word).
 *
 * `DOMParser` then yields an inert document — no script execution, no resource
 * loads — so reading its `textContent` strips the remaining tags and decodes
 * entities in one safe step.
 */
const stripToText = (markup: string): string => {
  const spaced = escapeStrayLt(markup).replace(BLOCK_OR_BREAK_TAG, " ");
  return (
    new DOMParser().parseFromString(spaced, "text/html").body.textContent ?? ""
  );
};

/**
 * Convert authored feedback markup into plain text for a live region.
 *
 * The strip runs a second time when the first pass leaves something that still
 * looks like a tag. Authored content sometimes escapes its markup (`&lt;b&gt;`),
 * and decoding entities is inseparable from parsing tags — so the first pass
 * turns `&lt;b&gt;hi&lt;/b&gt;` into the *text* `<b>hi</b>`, which a screen
 * reader would read out as tag names. The second pass removes it. Plain text
 * (including science copy like "pH < 7", where `<` opens no tag) is untouched,
 * so the extra pass is a no-op for everything else.
 */
export const toSpokenText = (
  markup: string,
  labels: SpokenTextLabels = defaultSpokenTextLabels
): string => {
  if (typeof markup !== "string" || markup === "") return "";

  let text = stripToText(expandSupSub(markup, labels));
  if (TAG_START.test(text)) {
    text = stripToText(expandSupSub(text, labels));
  }

  return text.replace(/\s+/g, " ").trim();
};
